/**
 * Option model for the Lost Sales scope picker.
 *
 * Both grains share one normalized shape so the picker and the page never
 * branch on the grain's API row format:
 *   { key, productId, productName, sku, skus }
 * - sku grain: one option per (product, SKU); `sku` is set, `skus` = [sku].
 * - product grain: one option per product; `sku` is null, `skus` = child SKUs.
 */

const toId = (value) =>
  value && typeof value === "object" && value._id != null
    ? String(value._id)
    : value != null
      ? String(value)
      : "";

const cleanSku = (value) => (typeof value === "string" ? value.trim() : "");

const uniqueSkus = (values = []) => [
  ...new Set((values || []).map(cleanSku).filter(Boolean)),
];

export const scopeOptionKey = (grain, productId, sku) =>
  grain === "product" ? String(productId) : `${productId}:${sku}`;

/** Rows from POST /fetch-sku (one row per product variant) → SKU options. */
export const optionsFromCatalogSkuRows = (rows = []) => {
  const seen = new Set();
  const options = [];
  (rows || []).forEach((row) => {
    const productId = toId(row?._id);
    const sku = cleanSku(row?.finalSku || row?.variants?.sku || row?.sku);
    if (!productId || !sku) return;
    const key = scopeOptionKey("sku", productId, sku);
    if (seen.has(key)) return;
    seen.add(key);
    options.push({
      key,
      productId,
      productName: row?.name || row?.finalTitle || "",
      sku,
      skus: [sku],
    });
  });
  return options;
};

/** Rows from GET /product ($select name, variants) → product options. */
export const optionsFromCatalogProductRows = (rows = []) => {
  const seen = new Set();
  const options = [];
  (rows || []).forEach((row) => {
    const productId = toId(row?._id);
    if (!productId || seen.has(productId)) return;
    seen.add(productId);
    options.push({
      key: scopeOptionKey("product", productId),
      productId,
      productName: row?.name || "",
      sku: null,
      skus: uniqueSkus((row?.variants || []).map((variant) => variant?.sku)),
    });
  });
  return options;
};

export const optionPrimaryLabel = (option) =>
  option?.sku || option?.productName || option?.productId || "";

/**
 * Case-insensitive match on SKU and product name. Whitespace-separated terms
 * must all match (e.g. "khai 7" finds KHAI-PINK-7).
 */
export const filterScopeOptions = (options, query) => {
  const terms = String(query || "")
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (!terms.length) return options;
  return options.filter((option) => {
    const haystack = `${option.sku || ""} ${option.productName || ""}`.toLowerCase();
    return terms.every((term) => haystack.includes(term));
  });
};

/** Page scope shape consumed by the single/bulk payload builders. */
export const scopeFromOption = (option) => ({
  productId: option.productId,
  product: {
    _id: option.productId,
    name: option.productName || null,
    variants: (option.skus || []).map((sku) => ({ sku })),
  },
  sku: option.sku || null,
  childSkuCount: option.sku ? 1 : (option.skus || []).length,
});
